import React, { useMemo } from 'react'
import _, { assign, concat, get, isArray, max, slice } from 'lodash'
import moment from 'moment'
import ContainerWidthSizer from '../../../components/UI/ContainerWidthSizer'
import {
  VictoryChart,
  VictoryAxis,
  VictoryBar,
  VictoryScatter,
  VictoryLine,
  VictoryTooltip,
  VictoryVoronoiContainer
} from 'victory'
import { MicrobiomeChartContainer } from '../index.style'
import styled from 'styled-components'

const NoPercentMetrics = ['energy', 'digestion_ranking', 'mood', 'sleep']

const LevelDescription = styled.div`
  position: absolute;
  left: 53px;
  font-family: Gilroy;
  font-weight: 500;
  line-height: 11px;
  font-size: 10px;
  color: #97a0a7;
  letter-spacing: 0.3px;
  &.top {
    top: 20px;
  }
  &.bottom {
    top: 190px;
    max-width: 69px;
  }
`

export default function DynamicRangeChart(props) {
  const {
    question,
    choices,
    details,
    subject,
    unit,
    className,
    selectTag,
    original_id
  } = props
  const noPercent = !!NoPercentMetrics.includes(original_id)
  const slider = question.slider

  const hoverRows = useMemo(() => {
    const answers = _.get(props, 'answers') || []
    if (_.get(props, 'type') === 'slider') {
      return answers.map(() => [''])
    } else {
      return answers.map((answer) => {
        const _an = _.get(answer, 'answer')
        if (isArray(_an)) {
          return concat(
            [''],
            _an.map((item) => item.value)
          )
        } else {
          return ['']
        }
      })
    }
  }, [props.answers])

  const { data, tickValuesX, tickValuesY, highlightData } = getMetaData()
  return (
    <MicrobiomeChartContainer className={`${className} ${selectTag}`}>
      <div className="header">
        <div className="name">
          {subject}{' '}
          {selectTag && (
            <span className={`${selectTag} tag`}> {selectTag}</span>
          )}
        </div>
        {getCurrentStatus()}
      </div>
      <div className="chart">
        <div className="chart-label">
          <div className="label-text">{unit}</div>
        </div>
        {slider && (
          <LevelDescription className="LevelDescription top">
            {slider.max_title}
          </LevelDescription>
        )}
        <ContainerWidthSizer>
          {({ width }) => (
            <VictoryChart
              width={width}
              height={220}
              minDomain={{ x: 0.5, y: tickValuesY[0] }}
              padding={{ top: 40, right: 20, bottom: 40, left: 60 }}
              containerComponent={<VictoryVoronoiContainer />}
            >
              <VictoryAxis
                tickValues={tickValuesX}
                tickFormat={(t) => {
                  if (data[t - 1]) {
                    return moment(
                      data[t - 1].completion_date || data[t - 1].date
                    ).format('MMM DD')
                  } else {
                    return null
                  }
                }}
                style={{
                  tickLabels: {
                    fill: '#70808E',
                    fontSize: 10, //device === 'mobile' ? 10 : 14,
                    fontFamily: 'Gilroy'
                  },
                  axis: {
                    strokeDasharray: '3,3',
                    stroke: 'rgba(0, 0, 0, 0.1)'
                  }
                }}
              />

              <VictoryAxis
                dependentAxis
                tickValues={tickValuesY}
                tickFormat={(t) => {
                  if (
                    props.type &&
                    props.type.toLowerCase().includes('percentage') &&
                    t === 100
                  ) {
                    return t + '%'
                  }
                  return t.toString()
                }}
                style={{
                  axis: {
                    stroke: 'rgba(0, 0, 0, 0.1)'
                  },
                  grid: {
                    strokeDasharray: '3,3',
                    stroke: 'rgba(0, 0, 0, 0.1)'
                  },
                  tickLabels: {
                    fill: (e) => {
                      for (const { range, score } of choices) {
                        const [start, end] = range
                        if (Number(e) >= start && Number(e) <= end) {
                          return getStatus(score)
                        }
                      }
                      return '#52606C'
                    },
                    padding: 15,
                    fontSize: 10,
                    fontFamily: 'Gilroy',
                    fontWeight: 600
                  }
                }}
              />

              <VictoryBar
                barWidth={17}
                data={highlightData}
                labels={() => ['Nutrition', 'Questionnaire']}
                style={{
                  data: {
                    fill: 'rgba(217,217,217,0.2)'
                  },
                  labels: {
                    fontSize: 8,
                    fontWeight: 700,
                    fontFamily: 'Gilroy'
                  }
                }}
              />

              <VictoryBar
                barWidth={17}
                data={highlightData.map(({ x, y }) => ({
                  x,
                  y: tickValuesY[0]
                }))}
                style={{
                  data: {
                    stroke: '#656F77',
                    strokeWidth: 3
                  },
                  labels: {
                    fontSize: 8,
                    fontWeight: 700,
                    fontFamily: 'Gilroy'
                  }
                }}
              />

              <VictoryLine
                data={data}
                style={{
                  data: { stroke: '#E2E5E9', strokeWidth: 2 }
                }}
              />

              <VictoryScatter
                style={{
                  labels: {
                    fontSize: 15,
                    fill: ({ datum }) => datum.status
                  }
                }}
                data={data}
                dataComponent={
                  <Dot
                    name="data-scatter"
                    maxY={_.cloneDeep(tickValuesY).pop()}
                  />
                }
                labels={({ datum }) => {
                  if (datum) {
                    return datum.score_value
                  }
                }}
              />

              <VictoryScatter
                data={data}
                dataComponent={<Dot name="data-scatter" noLabel />}
                labels={({ datum }) => {
                  if (datum) {
                    return datum.score_value
                  }
                }}
                labelComponent={
                  <VictoryTooltip
                    labelComponent={<HoverInfoPoint hoverRows={hoverRows} />}
                    dy={-15}
                    dx={110}
                    flyoutPadding={10}
                    flyoutHeight={30}
                    flyoutWidth={190}
                    pointerLength={0}
                    flyoutStyle={{
                      stroke: 'transparent',
                      strokeWidth: 2,
                      fill: 'transparent'
                    }}
                  />
                }
              />
            </VictoryChart>
          )}
        </ContainerWidthSizer>
        {slider && (
          <LevelDescription className="LevelDescription bottom">
            {slider.min_title}
          </LevelDescription>
        )}
      </div>
    </MicrobiomeChartContainer>
  )

  function getCurrentStatus() {
    const currentRecord = details[0] || {}
    let status = 'optimal'
    switch (currentRecord.score) {
      case 1:
        status = 'poor'
        break
      case 2:
        status = 'fair'
        break
      default:
        break
    }
    return <div className={status}>{status.toUpperCase()}</div>
  }

  function getMetaData() {
    let tickValuesY = [],
      rangeArr = [],
      data = []

    for (let i = 0, l = choices.length; i < l; i++) {
      const nextIndex = i + 1
      const { range, score } = choices[i]
      const nextChoice = choices[nextIndex]
      let _min, _max
      _min = range[0]
      if (nextChoice) {
        _max = nextChoice.range[0]
      } else if (range[1]) {
        _max = range[1]
      } else {
        _max = range[0] + (range[0] - choices[i - 1].range[0])
      }
      rangeArr.push({
        values: [_min, _max],
        score,
        status: getStatus(score)
      })
    }

    choices.forEach((e) => {
      tickValuesY = [...tickValuesY, ...e.range]
    })

    tickValuesY = _.uniq(tickValuesY)
      .filter((item) => _.isInteger(item))
      .sort((a, b) => a - b)

    data = _.cloneDeep(details)
      .reverse()
      .map((detail, index) => {
        const { range, date, score, score_value, answer, type } = detail
        const isVirtual = answer && typeof answer.value === 'string'
        let value
        if (_.isNumber(score_value) || detail.question_type === 'level_bar') {
          value = score_value || (!isVirtual && answer.value) || 0
        } else {
          if (range.length === 2) {
            value = (range[0] + range[1]) / 2
          } else {
            value = range[0]
          }
        }

        let obj = {
          y: value,
          x: index + 1,
          date: date || get(detail, 'completion_date'),
          score,
          score_value: value,
          status: getStatus(score),
          isVirtual,
          type
        }

        if (noPercent) {
          return obj
        } else {
          let percent = 0
          let percent_color
          if (index !== 0) {
            const previousValue = details[details.length - index].score_value
            if (previousValue === 0) {
              percent = score_value * 100
            } else {
              percent = ((score_value - previousValue) / previousValue) * 100
              percent = percent.toFixed(0)
            }
            percent_color = getPercentColor(
              previousValue,
              score_value,
              rangeArr
            )
          } else {
            percent_color = getPercentColor(null, score_value, rangeArr)
          }

          return assign(obj, { percent, percent_color })
        }
      })

    let n = 8

    if (data.length > n) {
      data = data.slice(data.length - n)
      data.forEach((item, index) => {
        item.x = index + 1
      })
    }
    const tickValuesX = new Array(n + 1).fill(0).map((_, index) => index)

    return {
      tickValuesX,
      data,
      highlightData: data
        .filter((item) => item.type === 'nutrition_questionnaire')
        .map(({ x, y, status }) => ({
          x,
          y: max(tickValuesY),
          status: transformStatus(status)
        })),
      tickValuesY,
      stackDomain: []
    }
  }

  function getPercentColor(prev, cur, range, cur_score) {
    const temp = range.find((e) => e.score === 3).values
    if (prev === cur || prev === null) {
      if (cur_score === 3) {
        return '#40C47C'
      } else {
        return '#FF6B00'
      }
    }

    const average = (temp[0] + temp[1]) / 2
    const riseValue = cur - prev
    if (prev >= average) {
      // if previous score is greater than average, current score is better to down
      if (riseValue <= 0 && cur >= temp[0] && cur < temp[1]) {
        return '#40C47C'
      } else if (riseValue <= 0 && cur >= temp[1]) {
        return '#40C47C'
      } else {
        return '#FF6B00'
      }
    } else {
      // if previous score is greater than average, current score is better to up
      if (riseValue >= 0 && cur >= temp[0] && cur < temp[1]) {
        return '#40C47C'
      } else if (riseValue >= 0 && cur <= temp[0]) {
        return '#40C47C'
      } else {
        return '#FF6B00'
      }
    }
  }
}

function getStatus(score) {
  let status
  switch (score) {
    case 1:
      status = '#FF6B00'
      break
    case 2:
      status = '#F5BA40'
      break
    case 3:
      status = '#40C47C'
      break
    default:
      break
  }
  return status
}

function transformStatus(status) {
  switch (status) {
    case '#40C47C':
      return 'rgba(64,196,124,0.2)'
    case '#F5BA40':
      return 'rgba(245,186,64,0.2)'
    case '#FF6B00':
      return 'rgba(255,107,0,0.2)'
    default:
      return 'transparent'
  }
}

function Dot(props) {
  const {
    noLabel,
    maxY,
    datum: { isVirtual, score_value, status, type, y }
  } = props
  const dx = type === 'nutrition_questionnaire' && y === maxY ? 10 : 0
  return (
    <svg
      width="200"
      height="35"
      viewBox="0 0 200 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      x={props.x - 7}
      y={(props.y && props.y - 18) || 0}
    >
      {!noLabel && (
        <text
          style={{ textAnchor: 'middle' }}
          dy={-5 + dx}
          dx={dx}
          x={7}
          fontSize={9}
          fill={status}
        >
          {Number(score_value) ? Math.round(score_value) : score_value}
        </text>
      )}
      <circle
        cx="7"
        cy="5"
        r="4"
        fill={status} //"white"
        stroke={status}
        strokeWidth="2"
        strokeDasharray={isVirtual ? 2 : 0}
      />
    </svg>
  )
}

function HoverInfoPoint(props) {
  const { x, y, datum, hoverRows } = props
  const dx = -75,
    dy = -12
  return (
    <g>
      <rect
        dx={dx}
        dy={dy}
        x={x - 90}
        y={y - 32}
        height={41 + (hoverRows[datum.x - 1].length - 1) * 20}
        rx="6"
        width="190"
        stroke="#E6E9F2"
        strokeWidth="2"
        fill="white"
      />
      <circle
        cx={x - 70}
        cy={y - 11}
        r="3"
        stroke={datum.status}
        strokeWidth="2"
        fill={datum.status}
      />
      <text dx={dx} dy={dy} x={x + 17} y={y + 5} fontSize={13} fill="#323F4A">
        {moment(datum.date).format('MMM DD')}
      </text>

      <text
        dx={dx}
        dy={dy}
        x={datum.percent_color ? x + 100 : x + 160}
        y={y + 6}
        fontSize={13}
        fill={datum.status}
        textAnchor="end"
        width="100"
        style={{ fontWeight: 600 }}
      >
        {Number(datum.score_value)
          ? Math.round(datum.score_value)
          : datum.score_value}
      </text>
      {datum.percent_color && (
        <>
          <rect
            dx={dx}
            x={x + 30}
            y={y - 25}
            fontSize={13}
            height="26"
            rx="4"
            width="62"
            fill={datum.percent_color}
            style={{ opacity: 0.1 }}
          ></rect>
          {datum.percent !== 0 && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              x={x + 40}
              y={y - 15}
              // dy={-10}
              width="6"
              height="8"
              viewBox="0 0 4 6"
              fill="none"
            >
              <path
                fill={datum.percent_color}
                transform={datum.percent > 0 ? 'rotate(-180)' : ''}
                style={{ transformOrigin: 'center' }}
                dx={dx}
                // dy={dy}
                x={x + 120}
                y={y}
                fontSize={13}
                d="M3.7388 4.04064L4 3.74631L3.4776 3.15763L3.2164 3.45197L3.7388 4.04064ZM2 5.41133L1.7388 5.70571L2 6L2.26121 5.70571L2 5.41133ZM0.783612 3.45197L0.52241 3.15763L0 3.74631L0.261209 4.04064L0.783612 3.45197ZM3.2164 3.45197L1.7388 5.11695L2.26121 5.70571L3.7388 4.04064L3.2164 3.45197ZM2.26121 5.11695L0.783612 3.45197L0.261209 4.04064L1.7388 5.70571L2.26121 5.11695ZM2.3694 5.41133L2.3694 0L1.63061 0L1.63061 5.41133H2.3694Z"
              />
            </svg>
          )}
          <text
            dx={dx}
            dy={-7}
            x={x + 125}
            y={y}
            fontSize={13}
            fill={datum.percent_color}
            style={{ fontWeight: 600 }}
          >
            {Math.abs(datum.percent)}%
          </text>
        </>
      )}
      {slice(hoverRows[datum.x - 1], 1).map((value, index) => (
        <text
          key={index}
          dx={dx}
          dy={dy + index * 20}
          x={x}
          y={y + 29}
          fontSize={13}
          fill="#9AA5B1"
        >
          {value}
        </text>
      ))}
    </g>
  )
}
